"use client";

/**
 * THE action bar for an AI summary, everywhere one is shown.
 *
 * Replaces four divergent button rows (company/contact/deal card, inline
 * popover, ticket briefing, task briefing) that between them offered different
 * actions, in different orders, with different labels — and one bare Download
 * icon whose tooltip said "MP3" while every user read it as "download the
 * summary".
 *
 * Shape (MARKETING_MINIMALISM's progressive disclosure, and the reason the row
 * shrinks from five icons to two): ONE primary action — Copy — plus a single
 * "More" menu holding everything else, grouped Copy / Listen / Download / Send.
 * Regenerate stays outside the menu because it belongs to the panel, not to the
 * summary's content.
 *
 * Every destructive-looking ambiguity is now named in words: "Download summary
 * (Markdown)" versus "Download audio (MP3)".
 */

import { useCallback, useMemo } from "react";
import {
  Check,
  ChevronDown,
  Clipboard,
  Copy,
  Download,
  FileText,
  Loader2,
  Share2,
  Sparkles,
  Square,
  Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { useSummarySpeech } from "@/lib/hooks/ai/use-summary-speech";
import { summaryExportBaseName } from "@/lib/ai/summary-export/format";
import { cn } from "@/lib/utils";
import { useSummaryExport, type SummaryExportSubject } from "./use-summary-export";
import { summarySubjectSchema } from "@/lib/ai/summary-export/schema";

export interface SummaryActionsProps extends SummaryExportSubject {
  /** Entity the summary belongs to — drives the Slack canvas export payload. */
  entityType?: string;
  entityId?: string;
  /** Hide the Slack canvas item where no entity context exists. */
  allowSlackExport?: boolean;
  /** Rendered smaller inside compact panels (inline popover, list cards). */
  size?: "sm" | "default";
  className?: string;
}

export function SummaryActions({
  title,
  markdown,
  subject,
  sourceUrl,
  entityType,
  entityId,
  allowSlackExport = true,
  size = "sm",
  className,
}: SummaryActionsProps) {
  const getSubject = useCallback(
    (): SummaryExportSubject | null =>
      markdown.trim().length > 0 ? { title, markdown, subject, sourceUrl } : null,
    [title, markdown, subject, sourceUrl]
  );

  const { busy, error, errorCode, copied, copy, downloadFile, canShare, share } =
    useSummaryExport(getSubject);

  const {
    isLoading: isSpeechLoading,
    isDownloading: isAudioDownloading,
    isPlaying,
    error: speechError,
    toggle: toggleSpeech,
    download: downloadAudio,
  } = useSummarySpeech();

  const audioFilename = useMemo(() => summaryExportBaseName(title, subject), [title, subject]);
  const { openModal } = useGlobalModalStack();

  const hasContent = markdown.trim().length > 0;
  if (!hasContent) return null;

  const exportToSlackCanvas = (): void => {
    // Same validated subject the file export uses: the canvas route applies the
    // same title/length rules, so a payload that would 400 there is refused here.
    const validated = summarySubjectSchema.safeParse({
      title,
      markdown,
      ...(subject ? { subject } : {}),
      ...(sourceUrl ? { sourceUrl } : {}),
    });
    if (!validated.success) return;
    openModal("send-to-slack", {
      item: { id: entityId ?? "" },
      entityType: entityType || "contact",
      canvasMode: "summary",
      summaryMarkdown: validated.data.markdown,
      summaryTitle: validated.data.title,
    });
  };

  const combinedError = error ?? speechError;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {/* Primary action: one click, no menu. Copy is what people do most. */}
      <Button
        type="button"
        variant="ghost"
        size={size}
        onClick={() => void copy("text")}
        title="Copy the summary as plain text"
        aria-label="Copy summary as plain text"
      >
        {copied === "text" ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size={size}
            title="More ways to use this summary"
            aria-label="More summary actions"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Copy</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => void copy("text")}>
            <Clipboard className="mr-2 h-4 w-4" />
            Copy as plain text
            {copied === "text" && <Check className="ml-auto h-4 w-4 text-green-600" />}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void copy("markdown")}>
            <Clipboard className="mr-2 h-4 w-4" />
            Copy as Markdown
            {copied === "markdown" && <Check className="ml-auto h-4 w-4 text-green-600" />}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Listen</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault(); // keep the menu open while audio loads
              void toggleSpeech(markdown, { filename: audioFilename });
            }}
            disabled={isSpeechLoading}
          >
            {isSpeechLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : isPlaying ? (
              <Square className="mr-2 h-4 w-4" />
            ) : (
              <Volume2 className="mr-2 h-4 w-4" />
            )}
            {isPlaying ? "Stop reading" : "Read this to me"}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Download</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => void downloadFile("markdown")}
            disabled={busy !== null}
          >
            {busy === "markdown" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            Download summary (Markdown)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void downloadFile("text")} disabled={busy !== null}>
            {busy === "text" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            Download summary (plain text)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void downloadFile("pdf")} disabled={busy !== null}>
            {busy === "pdf" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            Download summary (PDF)
          </DropdownMenuItem>
          {/* Named "audio", never a bare "Download": that ambiguity is the bug. */}
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              void downloadAudio(markdown, { filename: audioFilename });
            }}
            disabled={isAudioDownloading || isSpeechLoading}
          >
            {isAudioDownloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download audio (MP3)
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Send</DropdownMenuLabel>
          {allowSlackExport && (
            <DropdownMenuItem onSelect={exportToSlackCanvas}>
              <Sparkles className="mr-2 h-4 w-4" />
              Export to a Slack canvas
            </DropdownMenuItem>
          )}
          {canShare && (
            <DropdownMenuItem onSelect={() => void share()}>
              <Share2 className="mr-2 h-4 w-4" />
              Share…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/*
        ERROR_STATES_ACTIONABLE: the message already names the remedy, and where
        the remedy is a BUTTON we render it here — a file export that failed on one
        format offers "Copy instead", which always works because it never leaves
        the browser.
      */}
      {combinedError && (
        <span className="flex items-center gap-2 text-xs text-destructive" role="status">
          {combinedError}
          {(errorCode === "SUMMARY_EXPORT_RENDER_FAILED" ||
            errorCode === "SUMMARY_EXPORT_TOO_LARGE") && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => void copy("text")}
            >
              Copy instead
            </Button>
          )}
        </span>
      )}
    </div>
  );
}
